import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { listAuditLogs } from '../services/auditLogsService';
import { AuditLog } from '../types/auditLog';

type Filters = {
  action: string;
  user: string;
  target_type: string;
  target_id: string;
};

type PaginatedAuditLogs = {
  data: AuditLog[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
  };
};

const initialFilters: Filters = {
  action: '',
  user: '',
  target_type: '',
  target_id: '',
};

export const AuditLogsPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [page, setPage] = useState(1);
  const [auditLogs, setAuditLogs] = useState<PaginatedAuditLogs | null>(null);
  const [loading, setLoading] = useState(false);

  const hasFilters = useMemo(
    () => Object.values(filters).some((value) => value.trim().length > 0),
    [filters],
  );

  const loadAuditLogs = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await listAuditLogs(token, {
        action: filters.action || undefined,
        user: filters.user || undefined,
        target_type: filters.target_type || undefined,
        target_id: filters.target_id ? Number(filters.target_id) : undefined,
        page,
      });
      setAuditLogs(response);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load audit logs.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page]);

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    loadAuditLogs();
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    loadAuditLogs();
  };

  const formatDate = (value: string) => new Date(value).toLocaleString();

  const renderDiff = (diff: Record<string, unknown> | null) => {
    if (!diff || Object.keys(diff).length === 0) {
      return '—';
    }

    return (
      <pre
        style={{
          margin: 0,
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
          background: 'rgba(15, 35, 95, 0.04)',
          padding: '8px',
          borderRadius: 8,
        }}
      >
        {JSON.stringify(diff, null, 2)}
      </pre>
    );
  };

  return (
    <AppLayout
      title="Audit Logs"
      toolbar={
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ width: 'auto', padding: '10px 18px', marginTop: 0 }}
            onClick={handleResetFilters}
            disabled={!hasFilters}
          >
            Clear filters
          </button>
        </div>
      }
    >
      <section className="card" style={{ padding: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <div className="form-group">
          <label htmlFor="filter-action">Action</label>
          <input
            id="filter-action"
            type="text"
            className="input"
            placeholder="auth.login.success"
            value={filters.action}
            onChange={(event) => handleFilterChange('action', event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="filter-user">User</label>
          <input
            id="filter-user"
            type="text"
            className="input"
            placeholder="name or email"
            value={filters.user}
            onChange={(event) => handleFilterChange('user', event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="filter-target-type">Target type</label>
          <input
            id="filter-target-type"
            type="text"
            className="input"
            placeholder="App\\Models\\Transaction"
            value={filters.target_type}
            onChange={(event) => handleFilterChange('target_type', event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="filter-target-id">Target ID</label>
          <input
            id="filter-target-id"
            type="number"
            className="input"
            placeholder="123"
            value={filters.target_id}
            onChange={(event) => handleFilterChange('target_id', event.target.value)}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            type="button"
            className="btn"
            style={{ width: 'auto', padding: '12px 24px' }}
            onClick={handleApplyFilters}
          >
            Apply filters
          </button>
        </div>
      </section>

      <section className="card" style={{ padding: 0 }}>
        <div className="table">
          <div className="table-head" style={{ gridTemplateColumns: '220px 150px 150px 120px 1.6fr 1.2fr' }}>
            <span>When</span>
            <span>User</span>
            <span>Action</span>
            <span>Target</span>
            <span>Diff</span>
            <span>Request info</span>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="table-row" style={{ gridTemplateColumns: '220px 150px 150px 120px 1.6fr 1.2fr', alignItems: 'flex-start' }}>
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                  <LoadingSkeleton count={1} height="20px" width="100%" />
                </div>
              ))}
            </>
          ) : auditLogs && auditLogs.data.length > 0 ? (
            auditLogs.data.map((log) => (
              <div
                key={log.id}
                className="table-row"
                style={{ gridTemplateColumns: '220px 150px 150px 120px 1.6fr 1.2fr', alignItems: 'flex-start' }}
              >
                <span>{formatDate(log.created_at)}</span>
                <span>
                  <strong>{log.user?.name ?? 'System'}</strong>
                  <br />
                  <span className="subtle-text">{log.user?.email ?? '—'}</span>
                </span>
                <span>{log.action}</span>
                <span>
                  {log.target_type || '—'}
                  <br />
                  <span className="subtle-text">{log.target_id ?? '—'}</span>
                </span>
                <span>{renderDiff(log.diff)}</span>
                <span>
                  <span className="subtle-text">IP: {log.ip_address ?? '—'}</span>
                  <br />
                  <span className="subtle-text">
                    Agent: {log.user_agent ? log.user_agent.substring(0, 60) : '—'}
                    {log.user_agent && log.user_agent.length > 60 ? '…' : ''}
                  </span>
                </span>
              </div>
            ))
          ) : (
            <div className="table-row" style={{ gridTemplateColumns: '1fr', padding: '40px 20px' }}>
              <span style={{ textAlign: 'center', color: 'var(--neutral-600)' }}>
                {hasFilters ? 'No audit entries found matching your filters.' : 'No audit entries found.'}
              </span>
            </div>
          )}
        </div>
        {auditLogs && auditLogs.meta.last_page > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px', gap: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '8px 14px', marginTop: 0 }}
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            <span className="subtle-text" style={{ alignSelf: 'center' }}>
              Page {auditLogs.meta.current_page} of {auditLogs.meta.last_page}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '8px 14px', marginTop: 0 }}
              disabled={page >= auditLogs.meta.last_page}
              onClick={() => setPage((prev) => Math.min(prev + 1, auditLogs.meta.last_page))}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </AppLayout>
  );
};

